import { useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Role, useAuthContext } from "@acto/web-common/auth";
import { basicLogger, LDContext, LDProvider, useLDClient } from "launchdarkly-react-client-sdk";
import { ActoGraphqlProvider } from "@acto/taskpane/core/data-access/graphql/ActoGraphqlProvider";
import { AnalyticsProvider } from "@acto/taskpane/core/analytics/AnalyticsProvider";
import { styled } from "@pigment-css/react";
import { useTranslations } from "@acto/web-common/locales";
import { Logo } from "@acto/web-common/components";
import { ManagementNavigationBar } from "./ui/Navigation/ManagementNavigationBar";
import { useFlagWithContext } from "@acto/web-common/feature-flags";
import { buildPylonSupportUrl, PYLON_HELP_CENTER_URL } from "@acto/web-common/support";

const NAVIGATION_WIDTH = "256px";

const PageWrapper = styled("div")({
  display: "flex",
  minHeight: "100vh",
});

const NavigationWrapper = styled("div")({
  position: "fixed",
  top: 0,
  left: 0,
  bottom: 0,
  zIndex: 1000,
});

const NavigationPlaceholder = styled("div")({
  width: NAVIGATION_WIDTH,
  flexShrink: 0,
});

const PageContent = styled("main")(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: "128px",
  backgroundColor: theme.managementView.colors.neutral[0],
}));

const WaitForLaunchDarklyClient = ({ children }: React.PropsWithChildren) => {
  const ldClient = useLDClient();

  if (!ldClient) {
    return null;
  }

  return children;
};

const AuthenticatedLayout = () => {
  const tNav = useTranslations("navigation");
  const { logout, tenantName, user } = useAuthContext();
  const location = useLocation();
  const navigate = useNavigate();
  const isWorkflowsEnabled = useFlagWithContext("management-view-workflows", false);
  const isApiKeysEnabled = useFlagWithContext("management-view-api-keys", false);
  const isAdmin = user?.hasRole(Role.ADMIN) ?? false;

  useEffect(() => {
    if (!isWorkflowsEnabled && location.pathname === "/workflows") {
      navigate("/team", { replace: true });
    }
    if ((!isApiKeysEnabled || !isAdmin) && location.pathname === "/api-keys") {
      navigate("/team", { replace: true });
    }
  }, [isWorkflowsEnabled, isApiKeysEnabled, isAdmin, location.pathname, navigate]);

  const navigationItems = [
    { label: tNav("team"), route: "/team" },
    { label: tNav("customers"), route: "/customers" },
  ];

  if (isWorkflowsEnabled) {
    navigationItems.push({ label: tNav("workflows"), route: "/workflows" });
  }

  if (isApiKeysEnabled && isAdmin) {
    navigationItems.push({ label: tNav("apiKeys"), route: "/api-keys" });
  }

  const handleContactSupport = () => {
    const url = buildPylonSupportUrl({
      name: user?.name ?? "",
      email: user?.email ?? "",
    });
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleOpenHelpCenter = () => {
    window.open(PYLON_HELP_CENTER_URL, "_blank", "noopener,noreferrer");
  };

  return (
    <PageWrapper>
      <NavigationWrapper>
        <ManagementNavigationBar
          companyName={tenantName}
          companyIcon={<Logo width={64} height={64} />}
          currentRoute={location.pathname}
          navigationItems={navigationItems}
          dropdownItems={[
            { label: tNav("contactSupport"), onSelect: handleContactSupport },
            { label: tNav("helpCenter"), onSelect: handleOpenHelpCenter },
            { label: tNav("logout"), onSelect: logout },
          ]}
          onNavigate={(route) => navigate(route)}
        />
      </NavigationWrapper>
      <NavigationPlaceholder />
      <PageContent>
        <Outlet />
      </PageContent>
    </PageWrapper>
  );
};

export const Layout = () => {
  const { user, accessToken } = useAuthContext();
  const isAdminOrManager = user?.hasRole(Role.SALES_MANAGER) || user?.hasRole(Role.ADMIN);

  useEffect(() => {
    if (user && !isAdminOrManager) {
      window.location.href = import.meta.env.VITE_WIDGET_URL;
    }
  }, [user, isAdminOrManager]);

  const launchDarklyContext: LDContext | undefined = user
    ? {
        kind: "multi",
        user: {
          key: user.id,
          email: user.email,
          name: user.name,
          roles: user.roles,
        },
        tenant: {
          key: user.tenantId,
        },
      }
    : undefined;

  if (user && !isAdminOrManager) {
    return null;
  }

  return (
    <ActoGraphqlProvider
      accessToken={accessToken}
      graphqlEndpoint={import.meta.env.VITE_ACTO_API_GRAPHQL_ENDPOINT}
      clientName="management"
    >
      <LDProvider
        clientSideID={import.meta.env.VITE_LAUNCHDARKLY_CLIENT_SIDE_ID}
        context={launchDarklyContext}
        options={{ logger: basicLogger({ level: "warn" }) }}
      >
        <WaitForLaunchDarklyClient>
          <AnalyticsProvider app="management">
            <AuthenticatedLayout />
          </AnalyticsProvider>
        </WaitForLaunchDarklyClient>
      </LDProvider>
    </ActoGraphqlProvider>
  );
};
